/**
 * « Mes attestations », côté élève (INV-L6, docs/12 §5).
 *
 * Trois choix d'écran qui ne se relisent pas dans le code :
 *
 * - **Les certificats révoqués restent affichés**, marqués comme tels. Les
 *   masquer ferait découvrir le retrait à son titulaire au moment où il le
 *   présente à quelqu'un.
 * - **Le numéro de série est en évidence, et copiable.** C'est lui qu'on donne
 *   à un recruteur, pas le PDF : la vérification passe par le numéro, et c'est
 *   ce qui rend la copie du document sans conséquence.
 * - **Le lien de vérification est affiché en clair.** Un lien qu'on peut lire
 *   avant de cliquer est un lien qu'on peut recopier dans un email.
 */
import { useTranslation } from "react-i18next";

import {
  openCertificatePdf,
  useMyCertificates,
  type Certificate,
} from "@/features/learning/api/useCertificates";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

function verificationUrl(serial: string): string {
  return `${window.location.origin}/certificates/verify/${serial}`;
}

export function MyCertificatesPage() {
  const { t, i18n } = useTranslation("learning");
  const certificates = useMyCertificates();

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(new Date(value));

  if (certificates.isPending) {
    return (
      <Skeleton className="mx-auto my-8 h-48 w-full max-w-4xl" label={t("certificates.title")} />
    );
  }
  if (certificates.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={certificates.error} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("certificates.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("certificates.subtitle")}</p>
      </header>

      {certificates.data.length === 0 ? (
        <Card title={t("certificates.title")}>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("certificates.empty")}</p>
        </Card>
      ) : (
        <ul className="space-y-4">
          {certificates.data.map((certificate) => (
            <li key={certificate.id}>
              <CertificateCard
                certificate={certificate}
                formatDate={formatDate}
                label={t}
                verifyHref={verificationUrl(certificate.serial)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CardProps {
  certificate: Certificate;
  formatDate: (value: string) => string;
  label: ReturnType<typeof useTranslation<"learning">>["t"];
  verifyHref: string;
}

function CertificateCard({ certificate, formatDate, label, verifyHref }: CardProps) {
  return (
    <Card title={certificate.course_title}>
      <div className="space-y-4">
        {!certificate.is_valid && (
          // **Le texte porte l'information, pas la couleur.** Un lecteur
          // d'écran ne voit pas le rouge, et un daltonien non plus : « cette
          // attestation a été retirée le … » se lit sans elle (WCAG 1.4.1).
          // Pas de `role="status"` — ce n'est pas un message qui survient, c'est
          // le contenu de la carte ; et ce rôle est déjà celui du squelette de
          // chargement, qui le rendrait ambigu.
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">
            {label("certificates.revoked", {
              date: certificate.revoked_at === null ? "" : formatDate(certificate.revoked_at),
            })}
            {certificate.revoke_reason !== null && ` — ${certificate.revoke_reason}`}
          </p>
        )}

        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400">
              {label("certificates.issuedOn")}
            </dt>
            <dd className="text-sm">{formatDate(certificate.issued_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 dark:text-slate-400">
              {label("certificates.serial")}
            </dt>
            <dd className="font-mono text-sm tracking-wide">{certificate.serial}</dd>
          </div>
        </dl>

        <p className="text-xs break-all text-slate-500 dark:text-slate-400">
          {label("certificates.shareHint")}{" "}
          <a href={verifyHref} className="text-brand-600 hover:underline">
            {verifyHref}
          </a>
        </p>

        {certificate.is_valid && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              void openCertificatePdf(certificate.id);
            }}
          >
            {label("certificates.download")}
          </Button>
        )}
      </div>
    </Card>
  );
}
